<template>
  <div class="problem-details">
    <!-- Problem Tab Content -->
    <v-card id="2-Problem Need" text-color="primary" elevation="0">
      <v-card-title class="title">
        <v-icon left>mdi-lightbulb-on-outline</v-icon>
        Problem Need</v-card-title
      >
      <v-container
        style="display: flex; align-items: center"
        class="problem-need"
      >
        <div v-if="loadingNeed" class="need-loading">
          <v-skeleton-loader
            type="paragraph"
            color="var(--backgroundColorLight)"
          ></v-skeleton-loader>
        </div>
        <div v-else-if="currentNeed" class="need-preview">
          <p>{{ currentNeed }}</p>
        </div>
        <div v-else class="need-preview-empty">
          <p>No need defined yet. Click the edit button to add one.</p>
        </div>
        <v-btn
          @click="openNeedModal"
          class="ml-auto"
          color="primary"
          variant="text"
        >
          <v-icon left>mdi-pencil</v-icon>
          Edit
        </v-btn>
      </v-container>
    </v-card>

    <!-- Living lab selection -->
    <v-card id="1-Decision Purpose" text-color="primary" elevation="0">
      <v-card-title class="title">
        <v-icon left>mdi-map-marker-radius</v-icon>
        Location
      </v-card-title>
      <div class="lab-map-container">
        <LivingLabSelection
          :selectedProblem="problemId"
          @selectedLab="onLabSelected"
        />
        <FirstMap :selectedLab="livingLab" class="first-map" />
      </div>
    </v-card>

    <!-- Building selection -->
    <v-card id="3-Design Space" text-color="primary" elevation="0">
      <v-card-title class="title">
        <v-icon left>mdi-office-building</v-icon>
        <div>Buildings and infrastructure considered</div>
      </v-card-title>
      <AutomaticBuilding
        :selectedLab="livingLab"
        :selectedLabName="labName"
        :selectedProblem="problemId"
      />
      <!-- <Visual3D /> -->
    </v-card>

    <v-card id="4-Simulation Requirements" text-color="primary" elevation="0">
      <v-card-title class="title">
        <v-icon left>mdi-account-group</v-icon>
        Stakeholders
      </v-card-title>
      <AutomaticStakeForm
        :selectedLab="livingLab"
        :selectedProblem="problemId"
      />
    </v-card>

    <v-card id="5-Modeling Choice" text-color="primary" elevation="0">
      <v-card-title class="title">
        <v-icon left>mdi-chart-line</v-icon>
        KPIs</v-card-title
      >
      <AutomaticKpiForm :selectedLab="livingLab" :selectedProblem="problemId" />
    </v-card>

    <!-- Need Modal -->
    <v-dialog v-model="needModalOpen" max-width="600">
      <v-card>
        <v-card-title>{{
          isNewProblem ? "Define Problem Need" : "Edit Problem Need"
        }}</v-card-title>
        <v-card-text>
          <NeedBox
            ref="needBox"
            :selectedProblem="problemId"
            :need="currentNeed"
            @needUpdated="onNeedUpdated"
            @cancel="closeNeedModal"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Success notification -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="snackbarTimeout"
      color="success"
    >
      <div
        style="display: flex; align-items: center; gap: 8px; cursor: pointer"
        @click="showSnackbar = false"
      >
        <v-icon left>mdi-check-circle</v-icon>
        Need updated successfully!
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import LivingLabSelection from "@/components/LivingLabSelection.vue";
import AutomaticKpiForm from "@/components/kpis//AutomaticKpiForm.vue";
import AutomaticStakeForm from "@/components/stakeholders/AutomaticStakeForm.vue";
import AutomaticBuilding from "@/components/buildings/AutomaticBuilding.vue";
import NeedBox from "@/components/NeedBox.vue";
import FirstMap from "@/components/FirstMap.vue";
// import Visual3D from "@/components/Visual3D.vue";
import apiClient from "@/plugins/apiService";

export default defineComponent({
  name: "ProblemDetails",
  components: {
    LivingLabSelection,
    AutomaticKpiForm,
    AutomaticStakeForm,
    AutomaticBuilding,
    NeedBox,
    FirstMap,
    // Visual3D,
  },
  props: {
    problemId: {
      type: [String, Number],
      required: true,
    },
    livingLab: {
      type: [String, Number],
      default: null,
    },
    labName: {
      type: String,
      default: null,
    },
    isNewProblem: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      needModalOpen: false,
      currentNeed: null,
      loadingNeed: true,
      showSnackbar: false,
      snackbarTimeout: 3000,
    };
  },
  emits: ["labSelected"],
  mounted() {
    this.fetchCurrentNeed();
    // Auto-open modal for new problems
    if (this.isNewProblem) {
      this.$nextTick(() => {
        this.needModalOpen = true;
        this.$nextTick(() => {
          this.$refs.needBox?.focusInput();
        });
      });
    }
  },
  methods: {
    onLabSelected(selectedLab) {
      this.$emit("labSelected", selectedLab);
    },
    openNeedModal() {
      this.needModalOpen = true;
      this.$nextTick(() => {
        this.$refs.needBox?.focusInput();
      });
    },
    closeNeedModal() {
      this.needModalOpen = false;
    },
    onNeedUpdated(need) {
      this.currentNeed = need;
      this.showSnackbar = true;
      this.closeNeedModal();
    },
    async fetchCurrentNeed() {
      if (this.problemId) {
        try {
          this.loadingNeed = true;
          const response = await apiClient.get(`/need/${this.problemId}`);
          const data = response.data;
          this.currentNeed = data.length > 0 ? data[0].need : "";
        } catch (error) {
          console.error("Error fetching needs:", error);
        } finally {
          this.loadingNeed = false;
        }
      } else {
        console.warn("No problem selected");
        this.loadingNeed = false;
      }
    },
  },
});
</script>

<style scoped>
.problem-details {
  width: 100%;
}

.first-map {
  margin-left: 0;
}

.lab-map-container {
  background: linear-gradient(
    180deg,
    var(--secondaryColorLighter) 0%,
    var(--backgroundColorLight) 100%
  );
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  padding: 20px;
  border-radius: 12px;
}

.problem-need {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.need-preview {
  color: var(--contrastColor);
  white-space: pre-line;
  line-height: 1.6;
  font-weight: bold;
}

.need-preview-empty {
  padding: 16px;
  color: var(--secondaryColor);
  font-style: italic;
}

.need-loading {
  flex: 1;
  min-height: 60px;
}
</style>
